import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, AlertTriangle, Loader2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * Wipes a demo/sandbox tenant's reports while preserving users/settings/branding.
 *
 * Flow:
 * 1. User clicks "Repor dados de teste" → dialog opens showing destructive summary.
 * 2. User clicks "Confirmar" → a 3-second countdown starts ("A repor em 3... 2... 1...").
 * 3. Countdown can be cancelled at any time with the "Cancelar reposição" button.
 * 4. At 0, `onConfirm` is called.
 *
 * @param {{
 *   open: boolean,
 *   onOpenChange: (o: boolean) => void,
 *   onConfirm: () => Promise<void>,
 *   deleting: boolean,
 *   tenant?: {name?: string, slug?: string, is_demo?: boolean} | null,
 * }} props
 */
export const ResetDemoDialog = ({ open, onOpenChange, onConfirm, deleting, tenant }) => {
  const COUNTDOWN_SECONDS = 3;
  const [count, setCount] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const timerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Reset local countdown state whenever the dialog closes.
  useEffect(() => {
    if (!open) {
      clearTimer();
      setConfirming(false);
      setCount(0);
    }
  }, [open]);

  const startCountdown = () => {
    setConfirming(true);
    setCount(COUNTDOWN_SECONDS);
    timerRef.current = setInterval(() => {
      setCount((c) => {
        if (c <= 1) {
          clearTimer();
          // defer the actual call so React paints "0" / closes first
          setTimeout(() => { onConfirm(); }, 50);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const abortCountdown = () => {
    clearTimer();
    setConfirming(false);
    setCount(0);
  };

  const slug = tenant?.slug || 'demo';
  const name = tenant?.name || slug;
  const label = tenant?.slug === 'demo' ? 'Demo' : 'teste';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!deleting) onOpenChange(o); }}>
      <DialogContent className="bg-slate-50 border-slate-200 text-slate-900" data-testid="reset-demo-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-indigo-700" /> Repor dados de teste ({label})
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Apaga <strong>todas as denúncias</strong>, mensagens, anexos (ficheiros físicos incluídos),
            notificações e <strong>histórico de emails enviados</strong> do tenant
            <span className="font-mono text-slate-800"> {name}</span> (<span className="font-mono">{slug}</span>).
            Os utilizadores, definições, branding e plano são preservados.
          </DialogDescription>
        </DialogHeader>

        <Alert className="bg-indigo-50 border-indigo-200">
          <AlertTriangle className="w-4 h-4 text-indigo-700" />
          <AlertDescription className="text-indigo-700">
            {tenant?.slug === 'demo'
              ? 'Esta ação não afeta clientes reais — apenas o tenant demo.'
              : 'Esta ação está disponível porque o tenant está marcado como "is_demo=true". Confirme que não é um cliente de produção antes de avançar.'}
          </AlertDescription>
        </Alert>

        {confirming && (
          <div
            className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-center gap-3 fade-in"
            data-testid="reset-demo-countdown"
          >
            <div className="w-12 h-12 rounded-full bg-amber-200 text-amber-900 font-bold text-2xl flex items-center justify-center tabular-nums shrink-0">
              {count}
            </div>
            <div className="flex-1">
              <div className="text-amber-900 font-semibold text-sm">
                A repor em {count}... {count > 1 ? `${count - 1}... ` : ''}
                {count > 2 ? '1...' : ''}
              </div>
              <div className="text-amber-800 text-xs mt-0.5">
                Clique em "Cancelar reposição" para abortar.
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {confirming ? (
            <Button
              onClick={abortCountdown}
              variant="outline"
              className="border-amber-400 text-amber-800 hover:bg-amber-100 font-semibold"
              data-testid="abort-reset-demo-btn"
            >
              <X className="w-4 h-4 mr-2" /> Cancelar reposição
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={deleting}
                className="border-slate-300 text-slate-600 hover:bg-slate-100"
                data-testid="cancel-reset-demo-btn"
              >
                Cancelar
              </Button>
              <Button
                onClick={startCountdown}
                disabled={deleting}
                className="bg-indigo-700 hover:bg-indigo-800 text-white"
                data-testid="confirm-reset-demo-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Repor dados de teste
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
